<template>
  <div class="download-page">
    <div class="download-container">
      <div class="download-card">
        <!-- 标题 -->
        <div class="download-header">
          <h1>{{ pageTitle }}</h1>
        </div>

        <!-- 文件列表 -->
        <div v-if="files.length > 0" class="download-file-list">
          <div class="file-list-content">
            <div v-for="(file, index) in files" :key="file.id" class="file-item">
              <div class="file-info">
                <span class="file-icon">📄</span>
                <div class="file-details">
                  <div class="file-name-wrapper">
                    <div class="file-name">{{ file.name }}</div>
                    <span class="file-size">
                      ({{ formatFileSize(file.size) }})
                    </span>
                  </div>
                </div>
              </div>
              <div class="file-actions">
                <span v-if="file.downloadStatus === 'preparing'" class="status-text status-preparing">准备中...</span>
                <button 
                  v-if="!file.downloadStatus || file.downloadStatus === 'ready'" 
                  @click.stop="downloadFile(file, index)" 
                  class="btn-icon-only download-btn-with-badge" 
                  title="下载"
                  :disabled="file.downloadStatus === 'preparing'"
                >
                  <img src="../assets/download2.png" alt="下载" class="btn-icon" />
                  <span v-if="file.downloadCount > 0" class="download-badge">{{ file.downloadCount > 99 ? '99+' : file.downloadCount }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 没有文件时显示提示 -->
        <div v-else class="empty-state">
          <div class="empty-icon">📁</div>
          <p class="empty-text">暂无文件</p>
        </div>

        <!-- 状态和设置 -->
        <div class="download-status">
          <div class="status-item">
            <span class="status-label">文件统计</span>
            <span class="status-value">{{ totalFileCount }}个文件 共{{ formatFileSize(totalFileSize) }}</span>
          </div>
          <div class="status-item">
            <span class="status-label">空间/文件有效期至</span>
            <span class="status-value">{{ expirationDate }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { showDialog } from '../Dialog/index.js'
import request from '../api/request.js'
import { createLogger } from '../util/logger.js'
import { formatFileSize } from '../util/file.js'
import uploadStorage from '../util/uploadStorage.js'

const logger = createLogger('Download')

const router = useRouter()

// 响应式数据
const files = ref([])
const expirationDate = ref('-')

// 计算属性：文件总数和总大小
const totalFileCount = computed(() => {
  return files.value.length
})

const totalFileSize = computed(() => {
  return files.value.reduce((total, file) => total + (file.size || 0), 0)
})

// 从路由参数获取配置
const pageTitle = ref('文件分享')

// 配置
const accessToken = ref('')

// 初始化参数
onMounted(async () => {
  // 优先从 sessionStorage 获取参数（按 code 区分）
  const storedCode = localStorage.getItem('lastAuthCode')
  const paramsKey = uploadStorage.getParamsKey()
  let storedParams = sessionStorage.getItem(paramsKey) || sessionStorage.getItem('uploadParams')
  logger.info('onMounted:', storedParams)

  // 解析并使用参数
  if (storedParams) {
    try {
      const params = JSON.parse(storedParams)

      // 设置参数
      if (params.items) files.value = params.items
      if (params.accessToken) accessToken.value = params.accessToken
      if (params.expirationDate) expirationDate.value = params.expirationDate

      // 清除 sessionStorage 中的参数（按 code 清除，并兼容旧 key）
      sessionStorage.removeItem(paramsKey)
      sessionStorage.removeItem('uploadParams')
    } catch (error) {
      logger.error('解析 sessionStorage 参数失败:', error)

      showDialog({
        title: '解析参数失败',
        message: "请联系商家处理！",
        type: 'error'
      })
      router.push('/share')
      return
    }
  } else {
    router.push('/share')
    return
  }

  // 配置请求基础URL和认证信息
  if (accessToken.value) {
    request.setDefaultHeaders({
      'Authorization': accessToken.value
    })
  }
})

// 下载文件
const downloadFile = async (fileItem, index) => {
  try {
    logger.debug('下载文件:', fileItem)
    if (!fileItem.fileId) {
      throw new Error('数据异常，暂无法下载')
    }
    
    // 立即显示"准备中..."状态，提升用户体验
    fileItem.downloadStatus = 'preparing'
    
    // 先检查是否已有保存的下载URL
    let downloadUrl = fileItem.downloadUrl

    // 如果没有保存的URL，才请求获取
    if (!downloadUrl || downloadUrl === "") {
      downloadUrl = await request.getFileDownloadUrl(fileItem)
      
      if (!downloadUrl || downloadUrl === "") {
        fileItem.downloadStatus = null
      throw new Error('下载URL为空')
      }

      // 保存下载URL到文件对象，避免下次重复请求
      fileItem.downloadUrl = downloadUrl
    }

    // 增加下载次数
    fileItem.downloadCount = (fileItem.downloadCount || 0) + 1

    const result = await request.downloadFile(downloadUrl, fileItem.name)
    
    if (!result.success) {
      fileItem.downloadStatus = null
      throw new Error(result.error || '下载失败')
    }

    // 延迟清除状态，让用户看到"准备中..."提示
    setTimeout(() => {
      fileItem.downloadStatus = null
    }, 1000)
    
  } catch (error) {
    logger.error('下载失败:', error)
    fileItem.downloadStatus = null
    showDialog({
      title: '下载失败',
      message: `文件 "${fileItem.name}" 下载失败: ${error.message}`,
      type: 'error'
    })
  }
}

</script>

<style scoped>
.download-page {
  min-height: 100vh;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
}

.download-container {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
}

.download-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 600px;
}

.download-header {
  margin-bottom: 1rem;
}

.download-header h1 {
  margin: 0;
  color: #333;
  font-size: 1.5rem;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 3rem 1.5rem;
  color: #999;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-text {
  font-size: 16px;
  margin: 0;
}

/* 文件列表样式 */
.download-file-list {
  width: 100%;
  border-radius: 12px;
  margin-bottom: 1rem;
  height: 300px;
}

.download-file-list .file-list-content {
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.download-file-list .file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  transition: all 0.2s ease;
  flex-shrink: 0;
  box-sizing: border-box;
}

.download-file-list .file-item:last-child {
  margin-bottom: 0;
}

.file-list-content {
  display: flex;
  flex-direction: column;
}

.file-list-content::-webkit-scrollbar {
  width: 6px;
}

.file-list-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.file-list-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.file-list-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  background: white;
  transition: background-color 0.2s;
  min-height: 2.5rem;
}

.file-item:last-child {
  border-bottom: none;
}

.file-item:hover {
  background: #f8f9fa;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  padding-right: 1rem;
}

.file-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.file-details {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
}

.file-name {
  font-weight: 500;
  color: #333;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  width: 100%;
  max-width: 100%;
}

.file-size {
  font-size: 12px;
  color: #666;
  font-weight: normal;
  white-space: nowrap;
  text-align: left;
  flex: 1;
  min-width: 0;
}

.file-name-wrapper {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.file-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-shrink: 0;
}

.btn-icon-only {
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #666;
}

.btn-icon-only:hover {
  background: #f8f9fa;
  transform: scale(1.1);
}

.btn-icon-only:active {
  transform: scale(0.95);
}

.btn-icon {
  width: 24px;
  height: 24px;
}

.status-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.status-item:last-child {
  border-bottom: none;
}

.status-label {
  color: #333;
  font-size: 14px;
}

.status-value {
  color: #666;
  font-size: 14px;
}

.download-status {
  margin-top: 1rem;
}

.download-btn-with-badge {
  position: relative;
}

.status-text {
  font-size: 12px;
  color: #666;
  padding: 0.25rem 0.5rem;
  white-space: nowrap;
}

.status-preparing {
  color: #ff9800;
}

.btn-icon-only:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-icon-only:disabled:hover {
  background: none;
  transform: none;
}

.download-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 16px;
  height: 16px;
  line-height: 16px;
  padding: 0 4px;
  background-color: #f56c6c;
  color: #fff;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  box-shadow: 0 0 0 1px #fff;
  z-index: 1;
}

@media (max-width: 768px) {
  .download-container {
    padding: 1rem;
  }

  .download-card {
    padding: 1rem;
  }

  .file-item {
    padding: 0.4rem 0.75rem;
    min-height: 2rem;
  }

  .file-actions {
    gap: 0.4rem;
  }

  .btn-icon {
    width: 14px;
    height: 14px;
  }

  .btn-icon-only {
    padding: 0.2rem;
    font-size: 11px;
  }

  .file-name {
    max-width: 120px;
    font-size: 13px;
  }

  .file-size {
    font-size: 11px;
  }
}
</style>
